import { useImperativeHandle, useLayoutEffect, useState } from "react";
import type { JSX, Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useIslandHost } from "@frontend/contexts";

import { Pagination } from "./Pagination";
import {
  type PaginationDataProps,
  paginationDataSchema,
} from "./Pagination.schema";
import { PAGINATION_TAG_NAME } from "./constants";

export interface PaginationHandle {
  setPage: (page: number) => void;
  setPageCount: (pageCount: number) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [PAGINATION_TAG_NAME]: {
      props: PaginationDataProps;
      handle: PaginationHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-pagination": HTMLElement & PaginationHandle;
  }
}

export function RWCPagination({
  ref,
  page: initialPage,
  pageCount: initialPageCount,
  ...props
}: PaginationDataProps & { ref?: Ref<PaginationHandle> }): JSX.Element {
  const host = useIslandHost();

  const [page, setPage] = useState(initialPage);
  const [pageCount, setPageCount] = useState(initialPageCount);

  useImperativeHandle(ref, () => ({ setPage, setPageCount }), []);

  useLayoutEffect(() => setPage(initialPage), [initialPage]);
  useLayoutEffect(() => setPageCount(initialPageCount), [initialPageCount]);

  function handlePageChange(nextPage: number) {
    setPage(nextPage);
    host?.setAttribute("page", String(nextPage));

    host?.dispatchEvent(
      new CustomEvent("pagination:change", {
        detail: { page: nextPage },
        bubbles: true,
      }),
    );
  }

  function handlePagePrefetch(nextPage: number) {
    host?.dispatchEvent(
      new CustomEvent("pagination:prefetch", {
        detail: { page: nextPage },
        bubbles: true,
      }),
    );
  }

  return (
    <Pagination
      {...props}
      page={page}
      pageCount={pageCount}
      onPageChange={handlePageChange}
      onPagePrefetch={handlePagePrefetch}
    />
  );
}

componentManager.register(
  rwc(PAGINATION_TAG_NAME, {
    component: RWCPagination,
    dataSchema: paginationDataSchema,
  }),
);
